import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  limit as fbLimit,
} from 'firebase/firestore'
import { firestore, iamAuth } from '@/lib/firebase'
import { BudgetMonthSchema, type BudgetMonth } from './types'

/**
 * Firestore CRUD for `users/{uid}/budgets/{uuid}`.
 *
 * Same partition as iOS (see `Budget/User/BudgetRepositories.swift`
 * and the Firestore rules). Direct client writes are gated by the
 * rules — a user can only write under their own uid path.
 *
 * Doc id is a UUID per month; we look up by (year, month) fields
 * because that's how iOS stores them. A future refactor could move
 * to a deterministic doc id like `${year}-${month}` but that would
 * require migrating existing iOS docs.
 */

function requireUid(): string {
  const uid = iamAuth.currentUser?.uid
  if (!uid) throw new Error('Not signed in')
  return uid
}

function budgetsCollection(uid: string) {
  return collection(firestore, 'users', uid, 'budgets')
}

export async function fetchBudgetMonth(year: number, month: number): Promise<BudgetMonth | null> {
  const uid = requireUid()
  const q = query(
    budgetsCollection(uid),
    where('year', '==', year),
    where('month', '==', month),
    fbLimit(1),
  )
  const snap = await getDocs(q)
  if (snap.empty) return null
  const data = snap.docs[0].data()
  // Don't crash on a slightly-different shape from iOS — Zod parses
  // and applies defaults; anything unrecognized is dropped silently
  // (good for forward-compat when iOS adds fields).
  const parsed = BudgetMonthSchema.safeParse(data)
  if (!parsed.success) {
    // Soft-fail with a logged warning so we don't lock the user out
    // of their budget over a single bad doc. The fix lands in a
    // schema migration once we identify what's drifted.
    console.warn('budget month doc failed parse, treating as empty:', parsed.error)
    return null
  }
  return parsed.data
}

/** All BudgetMonth docs for the user (a budget spans a few dozen at
 *  most). Skips docs that fail schema parse, same as the single read. */
export async function fetchAllBudgetMonths(): Promise<BudgetMonth[]> {
  const uid = requireUid()
  const snap = await getDocs(budgetsCollection(uid))
  const out: BudgetMonth[] = []
  for (const d of snap.docs) {
    const parsed = BudgetMonthSchema.safeParse(d.data())
    if (parsed.success) out.push(parsed.data)
  }
  return out
}

export async function saveBudgetMonth(month: BudgetMonth): Promise<void> {
  const uid = requireUid()
  const ref = doc(budgetsCollection(uid), month.id)
  await setDoc(ref, month, { merge: false })
}
